import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Trash2, UploadCloud, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const inputCls = "w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-dwc-charcoal outline-none focus:border-dwc-green";

export const uploadFile = async (file, token, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  const r = await axios.post(`${API}/api/store/admin/media/upload`, fd, {
    ...auth(token),
    onUploadProgress: (e) => onProgress && onProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
  });
  return r.data;
};

export const MediaAdmin = ({ token }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", text: "", media_type: "photo" });
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/api/store/admin/stories`, auth(token)).then((r) => setItems(r.data)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a photo or video file"); return; }
    try {
      setProgress(0);
      const up = await uploadFile(file, token, setProgress);
      await axios.post(`${API}/api/store/admin/stories`, {
        ...form,
        media_url: up.url,
        media_type: form.media_type === "review" ? "review" : up.media_type,
        active: true,
      }, auth(token));
      toast.success("Media published");
      setForm({ name: "", location: "", text: "", media_type: "photo" });
      fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Upload failed");
    }
    setProgress(null);
  };

  const del = async (it) => {
    if (!window.confirm("Delete this media item?")) return;
    await axios.delete(`${API}/api/store/admin/stories/${it.id}`, auth(token));
    load();
  };

  return (
    <div className="mt-6 space-y-6" data-testid="media-admin-panel">
      <form onSubmit={submit} className="rounded-2xl border border-gray-100 bg-white p-6" data-testid="media-upload-form">
        <h3 className="text-sm font-extrabold uppercase tracking-wide text-dwc-charcoal">Upload Student Media</h3>
        <p className="mt-1 text-xs text-dwc-charcoal/55">Driving videos, review videos and customer photos. Published items appear on the Success Stories page.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <input type="file" ref={fileRef} accept="image/*,video/mp4,video/webm,video/quicktime" data-testid="media-file-input" className={`${inputCls} sm:col-span-2`} />
          <select value={form.media_type} onChange={(e) => setForm((f) => ({ ...f, media_type: e.target.value }))} data-testid="media-type-select" className={inputCls}>
            <option value="photo">Customer Photo</option>
            <option value="video">Driving Video</option>
            <option value="review">Review Video</option>
          </select>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Student name (optional)" data-testid="media-name-input" className={inputCls} />
          <input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="Location (optional)" className={inputCls} />
          <input value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} placeholder="Caption / review text (optional)" className={inputCls} />
        </div>
        <button type="submit" disabled={progress !== null} data-testid="media-upload-btn" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-dwc-green px-5 py-3 text-xs font-bold uppercase tracking-wide text-white hover:bg-dwc-green-dark disabled:opacity-60">
          {progress !== null ? (<><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Uploading {progress}%</>) : (<><UploadCloud className="h-4 w-4" aria-hidden="true" /> Upload & Publish</>)}
        </button>
      </form>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="media-list">
        {items.map((it) => (
          <div key={it.id} className="relative overflow-hidden rounded-xl border border-gray-100 bg-white" data-testid={`media-item-${it.id}`}>
            {it.media_type === "photo" ? (
              <img src={it.media_url} alt={it.name || "Student media"} loading="lazy" className="aspect-square w-full object-cover" />
            ) : (
              <video src={it.media_url} preload="metadata" muted className="aspect-square w-full bg-dwc-charcoal object-cover" />
            )}
            <div className="flex items-center justify-between p-2.5">
              <span className="truncate text-[11px] font-bold text-dwc-charcoal">{it.name || it.media_type}</span>
              <button onClick={() => del(it)} aria-label="Delete media" data-testid={`media-delete-${it.id}`} className="rounded p-1.5 text-dwc-crimson hover:bg-dwc-crimson/5"><Trash2 className="h-4 w-4" aria-hidden="true" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-full rounded-2xl bg-white p-8 text-center text-sm text-dwc-charcoal/50">No uploaded media yet.</p>}
      </div>
    </div>
  );
};
